import * as React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type PaginationState,
  flexRender,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * Base Table Components
 */
const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    /**
     * Make row selectable/clickable
     */
    clickable?: boolean;
  }
>(({ className, clickable, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      clickable && 'cursor-pointer',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('p-4 align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

/**
 * Sortable Table Header Component
 */
interface SortableHeaderProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /**
   * Whether this column is sorted
   */
  sorted?: false | 'asc' | 'desc';
  /**
   * Click handler for sorting
   */
  onSort?: () => void;
  /**
   * Whether sorting is enabled
   */
  canSort?: boolean;
}

const SortableTableHead = React.forwardRef<HTMLTableCellElement, SortableHeaderProps>(
  ({ className, sorted, onSort, canSort, children, ...props }, ref) => {
    return (
      <TableHead
        ref={ref}
        className={cn(canSort && 'cursor-pointer select-none', className)}
        onClick={canSort ? onSort : undefined}
        {...props}
      >
        <div className="flex items-center gap-2">
          {children}
          {canSort && (
            <span className="ml-auto">
              {sorted === 'asc' && <ChevronUp className="h-4 w-4" />}
              {sorted === 'desc' && <ChevronDown className="h-4 w-4" />}
              {!sorted && <ChevronsUpDown className="h-4 w-4 opacity-50" />}
            </span>
          )}
        </div>
      </TableHead>
    );
  },
);
SortableTableHead.displayName = 'SortableTableHead';

/**
 * Data Table Component with TanStack Table
 */
export interface DataTableProps<TData> {
  /**
   * Column definitions
   */
  columns: ColumnDef<TData>[];
  /**
   * Data array
   */
  data: TData[];
  /**
   * Enable pagination
   */
  enablePagination?: boolean;
  /**
   * Enable sorting
   */
  enableSorting?: boolean;
  /**
   * Enable filtering
   */
  enableFiltering?: boolean;
  /**
   * Initial page size
   */
  pageSize?: number;
  /**
   * Empty state message
   */
  emptyMessage?: string;
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Custom loading component
   */
  loadingComponent?: React.ReactNode;
  /**
   * Row click handler
   */
  onRowClick?: (row: TData) => void;
  /**
   * Table className
   */
  className?: string;
  /**
   * Wrapper className
   */
  wrapperClassName?: string;
}

/**
 * DataTable Component
 *
 * A powerful data table component with sorting, pagination, and filtering.
 * Built on TanStack Table (React Table v8).
 *
 * @example
 * const columns: ColumnDef<Patient>[] = [
 *   {
 *     accessorKey: 'name',
 *     header: 'Name',
 *   },
 *   {
 *     accessorKey: 'age',
 *     header: 'Age',
 *   },
 *   {
 *     accessorKey: 'diagnosis',
 *     header: 'Diagnosis',
 *   },
 * ];
 *
 * <DataTable
 *   columns={columns}
 *   data={patients}
 *   enablePagination
 *   enableSorting
 *   pageSize={10}
 * />
 *
 * @example
 * // With row click handler
 * <DataTable
 *   columns={columns}
 *   data={patients}
 *   onRowClick={(patient) => navigate(`/patients/${patient.id}`)}
 * />
 */
function DataTable<TData>({
  columns,
  data,
  enablePagination = false,
  enableSorting = true,
  enableFiltering = false,
  pageSize = 10,
  emptyMessage = 'No results found.',
  loading = false,
  loadingComponent,
  onRowClick,
  className,
  wrapperClassName,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting && {
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: setSorting,
    }),
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
    }),
    ...(enableFiltering && {
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters,
    }),
    state: {
      sorting,
      columnFilters,
      pagination,
    },
  });

  if (loading && loadingComponent) {
    return <div className={wrapperClassName}>{loadingComponent}</div>;
  }

  return (
    <div className={cn('space-y-4', wrapperClassName)}>
      <div className="rounded-md border">
        <Table className={className}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();

                  const sortHandler = header.column.getToggleSortingHandler();
                  return (
                    <SortableTableHead
                      key={header.id}
                      canSort={canSort}
                      sorted={sorted}
                      onSort={sortHandler ? () => sortHandler({}) : undefined}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </SortableTableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  clickable={!!onRowClick}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {enablePagination && (
        <DataTablePagination
          currentPage={table.getState().pagination.pageIndex + 1}
          totalPages={table.getPageCount()}
          pageSize={table.getState().pagination.pageSize}
          totalItems={data.length}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          onPreviousPage={() => table.previousPage()}
          onNextPage={() => table.nextPage()}
          onFirstPage={() => table.setPageIndex(0)}
          onLastPage={() => table.setPageIndex(table.getPageCount() - 1)}
          onPageSizeChange={(size) => table.setPageSize(size)}
        />
      )}
    </div>
  );
}

/**
 * Table Pagination Component
 */
export interface DataTablePaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onFirstPage: () => void;
  onLastPage: () => void;
  onPageSizeChange: (size: number) => void;
}

const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  canPreviousPage,
  canNextPage,
  onPreviousPage,
  onNextPage,
  onFirstPage,
  onLastPage,
  onPageSizeChange,
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>
          Showing {startItem} to {endItem} of {totalItems} results
        </span>
        <span className="hidden sm:inline">|</span>
        <label className="hidden items-center gap-2 sm:flex">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            {[5, 10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onFirstPage}
          disabled={!canPreviousPage}
          className="h-8 w-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          aria-label="Go to first page"
        >
          <ChevronsLeft className="h-4 w-4 mx-auto" />
        </button>
        <button
          type="button"
          onClick={onPreviousPage}
          disabled={!canPreviousPage}
          className="h-8 w-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          aria-label="Go to previous page"
        >
          <ChevronLeft className="h-4 w-4 mx-auto" />
        </button>
        <span className="text-sm text-muted-foreground">
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          onClick={onNextPage}
          disabled={!canNextPage}
          className="h-8 w-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          aria-label="Go to next page"
        >
          <ChevronRight className="h-4 w-4 mx-auto" />
        </button>
        <button
          type="button"
          onClick={onLastPage}
          disabled={!canNextPage}
          className="h-8 w-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          aria-label="Go to last page"
        >
          <ChevronsRight className="h-4 w-4 mx-auto" />
        </button>
      </div>
    </div>
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  SortableTableHead,
  DataTable,
  DataTablePagination,
};
